import { CHAIN_ID, CHAIN_PARAMS } from '../constants/constants';
import { Eip1193Provider } from './walletProvider';

/**
 * Запросы к кошельку. Каждая функция либо возвращает результат, либо бросает
 * ошибку с текстом для оператора: молча уходить в консоль нельзя — он
 * распоряжается реальными деньгами и должен видеть отказ.
 */

type WalletError = {
  code?: number;
  message?: string;
  data?: { originalError?: { code?: number } };
};

const errorCode = (error: unknown) => {
  const e = error as WalletError;
  return e?.code ?? e?.data?.originalError?.code;
};

const operatorMessage = (error: unknown, fallback: string): string => {
  switch (errorCode(error)) {
    case 4001:
      return 'Запрос отклонён в кошельке';
    case -32002:
      // Окно кошелька уже открыто (иногда свёрнуто), повторный запрос
      // отбрасывается — выглядит как «кнопка не работает».
      return 'Запрос уже открыт в кошельке — подтвердите его там';
    default:
      return `${fallback}: ${(error as WalletError)?.message ?? 'неизвестная ошибка'}`;
  }
};

/**
 * Кошелёк отдаёт hex-строку с негарантированным регистром, ethers — `bigint`.
 * Сравнивать их напрямую нельзя: `56 !== '0x38'`.
 */
export const normalizeChainId = (chainId: unknown): string | null => {
  if (chainId === null || chainId === undefined) return null;

  if (typeof chainId === 'string' && chainId.startsWith('0x')) {
    return chainId.toLowerCase();
  }

  return `0x${Number(chainId).toString(16)}`;
};

export const requestAccounts = async (
  provider: Eip1193Provider,
): Promise<string[]> => {
  let accounts: string[];

  try {
    accounts = await provider.request({ method: 'eth_requestAccounts' });
  } catch (error) {
    console.error('eth_requestAccounts:', error);
    throw new Error(operatorMessage(error, 'Не удалось подключить кошелёк'));
  }

  if (!accounts || accounts.length === 0) {
    throw new Error('Кошелёк не вернул ни одного аккаунта');
  }

  return accounts;
};

/** Молчаливая проверка: доступ уже выдан или нет. Окно кошелька не открывает. */
export const getAccounts = async (
  provider: Eip1193Provider,
): Promise<string[]> => {
  try {
    return (await provider.request({ method: 'eth_accounts' })) ?? [];
  } catch (error) {
    console.error('eth_accounts:', error);
    return [];
  }
};

export const getChainId = async (
  provider: Eip1193Provider,
): Promise<string | null> => {
  try {
    return normalizeChainId(await provider.request({ method: 'eth_chainId' }));
  } catch (error) {
    console.error('eth_chainId:', error);
    return null;
  }
};

export const switchToTargetChain = async (
  provider: Eip1193Provider,
): Promise<string> => {
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: CHAIN_ID }],
    });
  } catch (error) {
    // 4902 — сети нет в кошельке, её нужно сначала добавить.
    if (errorCode(error) !== 4902) {
      console.error('wallet_switchEthereumChain:', error);
      throw new Error(
        operatorMessage(error, 'Не удалось переключиться на BSC'),
      );
    }

    try {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: CHAIN_PARAMS,
      });
    } catch (addError) {
      console.error('wallet_addEthereumChain:', addError);
      throw new Error(
        operatorMessage(addError, 'Не удалось добавить сеть BSC'),
      );
    }
  }

  // Кошелёк отвечает успехом раньше, чем реально меняет сеть, — «переключились»
  // без проверки означало бы транзакцию в чужой сети.
  const chainId = await getChainId(provider);

  if (chainId !== CHAIN_ID) {
    throw new Error('Сеть в кошельке не переключилась на BSC');
  }

  return chainId;
};

/** Отзыв выданного доступа. Поддерживают не все кошельки — отказ не критичен. */
export const revokeAccountsPermission = async (provider: Eip1193Provider) => {
  try {
    await provider.request({
      method: 'wallet_revokePermissions',
      params: [{ eth_accounts: {} }],
    });
  } catch (error) {
    console.warn(
      'wallet_revokePermissions не поддержан:',
      (error as Error)?.message,
    );
  }
};
